/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Save/submit helpers for appointment registration.
 * @module logic/appointments/appointmentRegistrationSave
 */

import { logger, sanitizeError } from "../../utils/logger.js";
import { createAppointment, getUnconfirmedAppointmentCountForEvent } from "../appointment/index.js";
import {
  splitFormDataForUpdate,
  splitCompanyDataForUpdate,
  updateCompany,
  uploadCompanyFiles,
  DOCUMENT_KEYS,
} from "../companyEdit";

/**
 * Builds the appointment payload from merged company and form data.
 * @param {Object} options
 * @param {Object} options.participant
 * @param {Object} options.selectedRequirement
 * @param {Object} options.displayCompany
 * @param {Object} options.company
 * @param {Object} options.formData
 * @returns {{ appointmentData: Object, newFileFields: Object }}
 */
export const buildAppointmentRegistrationPayload = ({
  participant,
  selectedRequirement,
  displayCompany,
  company,
  formData,
}) => {
  const { regularFields, fileFields } = splitFormDataForUpdate(
    formData,
    DOCUMENT_KEYS,
  );
  const { companyRegularFields, companyFileFields } = splitCompanyDataForUpdate(
    displayCompany || company,
    DOCUMENT_KEYS,
  );

  const { _id, ...companyWithoutId } =
    { ...companyRegularFields, ...regularFields } || {};
  const newFileFields = { ...companyFileFields, ...fileFields } || {};
  const userId = localStorage.getItem("userId");

  const appointmentData = {
    appointmentInformation: {
      idEncuentroNegocios: participant.idEvento,
      idEmpresaCompradora: participant.idEmpresaCompradora,
      idUsuarioComprador: selectedRequirement.idUsuarioComprador,
      idUsuarioRemitente: userId,
      idRequerimiento: selectedRequirement._id,
      ...companyWithoutId,
      fileFields: Object.values(newFileFields || {}),
    },
  };

  return { appointmentData, newFileFields, regularFields, fileFields };
};

/**
 * Persists company changes when the user is not a no-socio and has edits.
 * @param {Object} options
 * @param {Object} options.company
 * @param {Object} options.regularFields
 * @param {Object} options.fileFields
 * @param {boolean} options.isNoSocio
 * @param {import("react-router").NavigateFunction} options.navigate
 * @returns {Promise<{updatedCompany: Object|null, uploadedFiles: boolean}>}
 */
export const persistRegistrationCompanyChanges = async ({
  company,
  regularFields,
  fileFields,
  isNoSocio,
  navigate,
}) => {
  const hasCompanyFieldChanges =
    Object.keys(regularFields).length > 0 ||
    Object.keys(fileFields).length > 0;

  if (!hasCompanyFieldChanges || isNoSocio) {
    return { updatedCompany: null, uploadedFiles: false };
  }

  const data = await updateCompany(
    company._id,
    regularFields,
    fileFields,
    navigate,
  );

  if (data.presignedUrls && Object.keys(fileFields).length > 0) {
    await uploadCompanyFiles(data.presignedUrls, fileFields);
  }

  return {
    updatedCompany: { ...data.company, _lastUpdate: Date.now() },
    uploadedFiles: true,
  };
};

/**
 * Creates the appointment and uploads any attached files.
 * @param {Object} options
 * @param {import("react-router").NavigateFunction} options.navigate
 * @param {Object} options.appointmentData
 * @param {Object} options.newFileFields
 * @returns {Promise<void>}
 */
export const createRegistrationAppointment = async ({
  navigate,
  appointmentData,
  newFileFields,
}) => {
  const appointmentResponse = await createAppointment(navigate, appointmentData);
  if (
    appointmentResponse.presignedUrls &&
    Object.keys(newFileFields).length > 0
  ) {
    await uploadCompanyFiles(
      appointmentResponse.presignedUrls,
      newFileFields,
    );
  }
};

/**
 * Checks event capacity and either saves directly or signals confirmation modal.
 * @param {Object} options
 * @param {import("react-router").NavigateFunction} options.navigate
 * @param {Object} options.eventData
 * @param {function(): Promise<void>} options.onSave
 * @returns {Promise<"save"|"confirm"|"past_due"|"blocked">}
 */
export const checkRegistrationCapacityAndProceed = async ({
  navigate,
  eventData,
  onSave,
}) => {
  try {
    const unconfirmedCount = await getUnconfirmedAppointmentCountForEvent(
      navigate,
      eventData._id,
    );

    if (unconfirmedCount >= eventData.cupoMaximo) {
      return "confirm";
    }

    await onSave();
    return "save";
  } catch (error) {
    logger.error("Error checking appointments:", {
      err: sanitizeError(error),
    });
    return "blocked";
  }
};
